import { Card, Group, Stack, Text } from '@mantine/core';
import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Sparkline } from './Sparkline';
import { MONEY_ROLE_VAR, type MoneyRole } from './moneyFormat';

/** Props for {@link StatTile}. */
export interface StatTileProps {
  /** Metric label rendered above the value in a small, dimmed style. */
  label: string;
  /** Primary metric value. Accepts any renderable node (string, number, formatted JSX). */
  value: ReactNode;
  /**
   * Delta text shown in the trend chip (e.g. `"+12.3%"`).
   * Only rendered when `trend` is also provided.
   */
  delta?: string;
  /** Direction of the trend; drives icon choice and chip colour (mint / amber — see the component docblock). */
  trend?: 'up' | 'down';
  /**
   * Secondary caption below the value (e.g. "vs last 30 days").
   *
   * Widened from `string` to `ReactNode` by vendor#444: the dashboard's
   * Inventory Value tile splits **inside the tile** — total on top, an On hand
   * line and a Vaulted line under it (`vendor-vault/notes.md` §8) — which needs
   * a stack and a chip, not a sentence. Rendered inside a dimmed `<Text>`, so a
   * node passed here should be phrasing-level content, not a block layout.
   */
  sub?: ReactNode;
  /** Optional sparkline data series rendered right-aligned at the bottom of the tile. */
  sparkline?: number[];
  /**
   * Accent colour applied to the sparkline.
   * Defaults to `var(--mantine-primary-color-filled)` (brand mint).
   */
  accent?: string;
  /**
   * `data-testid` placed on the value `<div>` so Playwright specs can assert
   * exact figure text (e.g. `"$50.00"`) without relying on brittle text-match
   * across the entire card. Only set this on tiles that are asserted in E2E tests.
   */
  valueTestId?: string;
  /**
   * Colours `value` with a {@link MoneyRole} token (`--money-in` /
   * `--money-out` / `--money-down` / `--money-flat`) instead of the default
   * ink. Leave unset for a non-money metric.
   */
  role?: MoneyRole;
  /**
   * Renders the label/value/sub stack WITHOUT the surrounding `<Card>` —
   * the design templates' bare "Metric" shape, used inline (e.g. inside a
   * larger composed card that supplies its own chrome) rather than as its
   * own tile. Ratified mapping: "Metric folds into StatTile as a variant"
   * (`docs/redesign-decision-brief.md` §2.2). Default `false` (card-wrapped,
   * today's behaviour, unchanged).
   */
  bare?: boolean;
}

/**
 * StatTile — KPI metric card used on dashboard and report pages.
 *
 * Shell: any (desktop / mobile). Avoid using in the kiosk shell — kiosk
 * surfaces use full-bleed browse tiles, not staff KPI cards.
 *
 * Structure:
 * - Top row: dim label (left) + optional trend chip (right)
 * - Middle: large numeric value (`--text-2xl` / bold / tight tracking)
 * - Bottom row: dim sub-caption (left) + optional sparkline (right, accent-coloured)
 *
 * Uses only Mantine layout components; no raw divs except for the value
 * container where precise typography control is needed. Its type comes from
 * the design system's scale rather than literals — `--text-2xl` is 24px, one
 * step down from the 26px this shipped with before #107; 26px is not on the
 * MTB scale and never was.
 *
 * Corrected 2026-08-04: this docblock used to cite "`KpiCard` in
 * `primitives.jsx` lines 122–150". `primitives.jsx` is one of the ghost sources
 * `mtb-design-system/platforms/alignment.md` §6 names — a design bundle in no
 * repo, with line numbers nobody can check. The design system replaced it, and
 * this component is the governed artifact now (#108).
 *
 * **Trend chip colour — decision recorded 2026-08-04 (vendor#227/#258/#274).**
 * PR #466 deliberately left this chip on `green-7`/`red-7` because it is a
 * design decision and money work was in flight. It is now wired to the
 * `--money-in` (mint) / `--money-down` (amber) tokens instead: `trend="down"`
 * on a money figure is a period that netted negative, and the design system
 * is explicit that **that is amber, never red** — red is reserved for broken
 * things, not for a transaction or a bad period. `--money-in`/`--money-out`/
 * `--money-down`/`--money-flat` had zero call sites before this change; this
 * is their first adoption. `bare`/`role` fold the design templates' "Metric"
 * primitive into this component as a variant rather than a parallel one, per
 * the ratified vocabulary mapping (`docs/redesign-decision-brief.md` §2.2).
 */
export function StatTile({
  label,
  value,
  delta,
  trend,
  sub,
  sparkline,
  accent = 'var(--mantine-primary-color-filled)',
  valueTestId,
  role,
  bare = false,
}: StatTileProps) {
  const trendColor = trend === 'up' ? 'var(--money-in)' : 'var(--money-down)';

  const content = (
    <Stack gap="sm">
      {/* Top row: label + trend chip */}
      <Group justify="space-between" align="center">
        <Text size="xs" fw={500} c="dimmed">
          {label}
        </Text>
        {trend != null && delta != null && (
          <Group
            gap={2}
            align="center"
            style={{
              color: trendColor,
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--weight-semibold)',
            }}
          >
            {trend === 'up' ? (
              <IconArrowUp size={12} stroke={2} aria-label="trend up" />
            ) : (
              <IconArrowDown size={12} stroke={2} aria-label="trend down" />
            )}
            <span>{delta}</span>
          </Group>
        )}
      </Group>

      {/* Value */}
      <div
        data-testid={valueTestId}
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--weight-bold)',
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 1,
          color: role != null ? MONEY_ROLE_VAR[role] : undefined,
        }}
      >
        {value}
      </div>

      {/* Bottom row: sub + sparkline */}
      <Group justify="space-between" align="flex-end">
        <Text size="xs" c="dimmed" component="div">
          {sub}
        </Text>
        {sparkline != null && sparkline.length > 0 && (
          <span style={{ color: accent }}>
            <Sparkline data={sparkline} fill />
          </span>
        )}
      </Group>
    </Stack>
  );

  if (bare) return content;
  return (
    <Card withBorder padding="md" radius="md">
      {content}
    </Card>
  );
}
